import { createTheme } from '@mui/material/styles';

/**
 * EL TEMA — anclado en el brand kit Looka #200766066 de la Fundacion.
 *
 *   Orange 3   #FF681F   naranja de marca
 *   Cape Cod   #3C4443   gris de marca (es el color del circulo del logo)
 *   Gallery    #EFEFEF   claro de marca
 *
 * El sitio se lee en dos modos y el lector elige: oscuro (por defecto, sostiene
 * mejor el logo y descansa la vista de noche) y claro (para leer de dia).
 * El color del rayo de cada entidad NO vive aqui: vive en config/rayos.js.
 */

export const MARCA = {
  naranja: '#FF681F',
  naranjaOscuro: '#D9500F',  // el mismo naranja bajado para que contraste sobre fondo claro
  gris: '#3C4443',
  claro: '#EFEFEF'
};

export const PALETAS = {
  oscuro: {
    fondo: '#1B2122',        // el gris de marca, oscurecido para leer largo
    superficie: '#232A2B',
    superficie2: '#2C3434',
    linea: '#3C4443',        // Cape Cod exacto
    texto: '#EFEFEF',        // Gallery
    // EL TEXTO DE LA CANALIZACION, un punto por debajo del blanco general
    // (Faina, 26-ago: «puedes bajarle el tono del blanco a la letra… quiero
    // ver»). Un paso: 12,4:1 en vez de 14,2:1. Sigue muy dentro del rango
    // comodo para leer largo —lo que cansa es bajar de 9— y le quita el filo
    // que tiene el blanco roto sobre fondo oscuro.
    textoLectura: '#E2E0DB',
    textoSuave: '#B9BEBC',
    textoTenue: '#7E8785',
    acento: MARCA.naranja,
    acentoTexto: '#1B2122'
  },
  claro: {
    fondo: '#F5F3EE',
    superficie: '#FFFFFF',
    superficie2: '#EAE7E0',
    linea: '#DAD6CD',
    texto: '#23292A',
    textoLectura: '#23292A',   // en fondo claro no hace falta bajarlo: ya son 13,3:1
    textoSuave: '#4E5756',
    textoTenue: '#7C8583',
    acento: MARCA.naranjaOscuro,
    acentoTexto: '#FFFFFF'
  }
};

export const FUENTES = {
  titulo: "'Jost', 'Century Gothic', Futura, sans-serif",
  lectura: "'Literata', Georgia, 'Times New Roman', serif"
};

/** Escribe la paleta como variables CSS para lo que no pasa por MUI. */
export const aplicarVariables = (modo) => {
  const p = PALETAS[modo] || PALETAS.oscuro;
  const raiz = document.documentElement;
  raiz.style.setProperty('--fondo', p.fondo);
  raiz.style.setProperty('--superficie', p.superficie);
  raiz.style.setProperty('--superficie-2', p.superficie2);
  raiz.style.setProperty('--linea', p.linea);
  raiz.style.setProperty('--texto', p.texto);
  raiz.style.setProperty('--texto-lectura', p.textoLectura || p.texto);
  raiz.style.setProperty('--texto-suave', p.textoSuave);
  raiz.style.setProperty('--texto-tenue', p.textoTenue);
  raiz.style.setProperty('--acento', p.acento);
  raiz.style.setProperty('--acento-texto', p.acentoTexto);
  raiz.style.setProperty('--fuente-titulo', FUENTES.titulo);
  raiz.style.setProperty('--fuente-lectura', FUENTES.lectura);
  raiz.setAttribute('data-modo', modo);
};

export const crearTema = (modo = 'oscuro') => {
  const p = PALETAS[modo] || PALETAS.oscuro;
  return createTheme({
    palette: {
      mode: modo === 'claro' ? 'light' : 'dark',
      primary: { main: p.acento, contrastText: p.acentoTexto },
      background: { default: p.fondo, paper: p.superficie },
      divider: p.linea,
      text: { primary: p.texto, secondary: p.textoSuave, disabled: p.textoTenue }
    },
    typography: {
      fontFamily: FUENTES.lectura,
      h1: { fontFamily: FUENTES.titulo, fontWeight: 300, letterSpacing: '-0.02em', lineHeight: 1.05 },
      h2: { fontFamily: FUENTES.titulo, fontWeight: 300, letterSpacing: '-0.015em', lineHeight: 1.1 },
      h3: { fontFamily: FUENTES.titulo, fontWeight: 400, letterSpacing: '-0.01em', lineHeight: 1.15 },
      h4: { fontFamily: FUENTES.titulo, fontWeight: 400, lineHeight: 1.2 },
      h5: { fontFamily: FUENTES.titulo, fontWeight: 400 },
      h6: { fontFamily: FUENTES.titulo, fontWeight: 500 },
      button: { fontFamily: FUENTES.titulo, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' },
      // etiquetas y datos: la tipografia de titulos, en pequeno y espaciada
      overline: { fontFamily: FUENTES.titulo, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase' }
    },
    shape: { borderRadius: 2 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 2, paddingInline: '1.4rem', paddingBlock: '.7rem' }
        }
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: p.fondo, color: p.texto }
        }
      }
    }
  });
};

// Se conserva el nombre anterior para no romper importaciones existentes.
export const theme = crearTema('oscuro');
